import React, {useEffect, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {deepEqual} from "../../../../core/services/utils/utils";
import CKEditorTextfield from "../../text-fields/ck-editor-textfield";
import {MuiRFF, Yup} from '../../form';


const schema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required),
    message: Yup.string().trim().nullable().required(ValidateMessages.required),
});


const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const UpsertProductNoteDialog = ({open, close, note, isForClient, submit}) => {
    const [submitting, setSubmitting] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    /**
     * With each change in note:
     * - sets the new values in initial values of the form.
     */
    useEffect(() => {
        setInitialValues({
            title: note?.title?.trim() ?? '',
            message: note?.msg?.trim() ?? '',
        });
    }, [note])

    /**
     * Creates or updates a given notes from the provided form values.
     *
     * @param {any} values form values
     * @return {Promise<void>}
     */
    const upsertNote = async (values) => {
        const forApi = {
            title: values.title?.trim(),
            message: values.message?.trim(),
            isForClient: isForClient,
        }
        if (note) {
            forApi['id'] = note.id;
        }
        if (deepEqual(initialValues, forApi)) {
            close();
            return;
        }
        setSubmitting(true);
        const response = await submit(forApi);
        if (!isMounted()) return;
        setSubmitting(false);
        if (response?.resultFlag) {
            close();
        }
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                loading={submitting}
                className={'bound-dialog upsert-product-note-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            !note
                                ? isForClient
                                    ? "Create Customer-side Note"
                                    : "Create Private Note"
                                : "Update Note"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={upsertNote}
                            id={'form-id'}
                            validate={validate}
                            initialValues={initialValues}
                            render={({values, form}) => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={'title'}
                                                placeholder={'title...'}
                                                label={"Title"}
                                                className={'mr-2'}
                                                required={required.title}
                                                value={values.title ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <CKEditorTextfield
                                                form
                                                name={'message'}
                                                required={required.message}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => close()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'form-id'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {

                                    !note
                                        ? submitting ? "Creating..." : "Create"
                                        : submitting ? "Updating..." : "Update"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default UpsertProductNoteDialog;
